import React, { useMemo } from 'react';

import { translateAndSortLanguages } from '@lumapps/languages/utils';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { useNavigationListFilters, useNavigationListFiltersDispatch } from '../../context/NavigationListFiltersContext';
import { actions } from '../../ducks/navigationAdminFilterSlice';
import { NAVIGATION_ADMIN } from '../../keys';
import { NavigationListFilterSelect, NavigationListFilterSelectChoice } from './NavigationListFilterSelect';

export interface LanguageFilterProps {
    label?: string;
    languages: Array<string>;
}

/**
 * Select component to select a user group to filter the navigation items to.
 */
export const LanguageFilter: React.FC<LanguageFilterProps> = ({ label, languages }) => {
    const { translateKey } = useTranslate();
    const { filters } = useNavigationListFilters();
    const dispatch = useNavigationListFiltersDispatch();

    /** Build the choices from all the group used in the navigation nodes  */
    const choices = React.useMemo<Array<NavigationListFilterSelectChoice>>(() => {
        const translatedLanguages = translateAndSortLanguages({ languages, translateKey });

        return translatedLanguages.map(({ key, label }) => ({ id: key, name: label || key }));
    }, [languages, translateKey]);

    const handleSelect = React.useCallback(
        (newValue) => {
            dispatch(
                actions.setFilter({
                    filter: 'language',
                    value: newValue?.id,
                }),
            );
        },
        [dispatch],
    );

    const selectedChoice = useMemo<NavigationListFilterSelectChoice | undefined>(
        () => (filters?.language ? choices.find(({ id }) => id === filters.language) : undefined),
        [choices, filters],
    );

    return (
        <NavigationListFilterSelect
            label={label || translateKey(NAVIGATION_ADMIN.FILTER_SHOW_IN)}
            // Hard code a count to use the plural form of "groups"
            placeholder={translateKey(GLOBAL.LANGUAGE)}
            choices={choices}
            selected={selectedChoice}
            onSelect={handleSelect}
            dataAttributeElement="filter-by-lang"
        />
    );
};
